/* eslint-disable no-param-reassign */
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector, useAppStore } from 'app/hooks';
import AttributeComponent from 'components/common/attribute';
import ProgressIndicator from 'components/common/progress-indicator';
import Checkbox from 'components/inputs/checkbox';
import NumberInput from 'components/inputs/number';
import Select from 'components/inputs/select';
import Text from 'components/inputs/text';
import Tooltip from 'components/layout/tooltip';
import SizingItems from 'components/structure-page/sizing/sizing-items';
import { dimensionsAttributeCodes } from 'data/constants';
import { defaultSizingAttributes } from 'data/sizing-calculator-defaults';
import {
  AttributeCodeType,
  AttributeType,
  ExposureDirectionType,
  LevelType,
  SectionType,
  SizingCalculatorAttributesType,
  SpaceType
} from 'data/types';
import {
  applyAttributesToEntireLevel,
  updateStructureItem
} from 'features/structure';
import { setActiveTipSequence } from 'features/ui';
import { produce } from 'immer';
import {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import { useImmer } from 'use-immer';
import { hasActiveItemUnsavedChanges } from 'utils/active-item-unsaved-changes';
import {
  filterAttributes,
  getAttributeValue,
  sortAttributes
} from 'utils/attributes';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { isNotEmpty } from 'utils/helpers';
import { getSizingOrder } from 'utils/sizing';
import {
  findLevelBySpaceId,
  findStructureItemById,
  isLevelConditioned,
  isLevelType,
  isSpaceType
} from 'utils/structure';
import { getSpaceDetailsSubSteps } from 'utils/structure-space-details';
import {
  buildFenestrationFocusTipSequence,
  isFenestrationFocusTipSequence
} from 'utils/tips';
import { errorModal } from 'utils/ui';
import WallsAroundSpace from './modals/walls-around-space';

type DefaultSizes = {
  [key: string]: number;
};

type ActiveItemAttributesProps = {
  autosave?: boolean;
  allowRenaming?: boolean;
  itemName?: string;
  currentSectionId?: string;
};

export interface ActiveItemAttributesRef {
  handleSave: () => void;
  hasUnsavedChanges: () => boolean;
}

const ActiveItemAttributes = forwardRef<
  ActiveItemAttributesRef,
  ActiveItemAttributesProps
>(
  (
    {
      autosave = false,
      allowRenaming: allowRename = false,
      itemName,
      currentSectionId
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const { items } = useAppSelector((state) => state.data);
    const { activeItem, activeSubStepIndex } = useAppSelector(
      (state) => state.ui
    );
    const { structure } = useAppSelector((state) => state.structure);
    const { projectSettings } = useAppSelector((state) => state.project);
    const [attributesBuffer, setAttributesBuffer] = useImmer<AttributeType[]>(
      []
    );
    const [sizingAttributesBuffer, setSizingAttributesBuffer] =
      useState<SizingCalculatorAttributesType>(defaultSizingAttributes);
    const [fenestrationBuffer, setFenestrationBuffer] = useImmer<number[]>([]);
    const [itemNameBuffer, setItemNameBuffer] = useState(itemName);
    const [defaultSizes, setDefaultSizes] = useState<DefaultSizes>({});
    const [length, setLength] = useState<number | undefined>();
    const [width, setWidth] = useState<number | undefined>();
    const [sqFt, setSqFt] = useState<number | undefined>();
    const [attributesToApplyToLevel, setAttributesToApplyToLevel] = useState<
      AttributeCodeType[]
    >([]);
    const [animate] = useAutoAnimate({ duration: 100 });
    const [focusedAttributeId, setFocusedAttributeId] = useState<number | null>(
      null
    );
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [conditionedBuffer, setConditionedBuffer] = useState<
      boolean | undefined
    >();
    const [additionalBTUsBuffer, setAdditionalBTUsBuffer] = useState<{
      cooling: number;
      heating: number;
    }>({ cooling: 0, heating: 0 });

    const updateSizingAttributes = (
      updates: Partial<SizingCalculatorAttributesType>
    ) => {
      setSizingAttributesBuffer((old) => ({
        ...old,
        ...updates
      }));
    };

    const homeOrientation = useMemo(() => {
      const orientationSetting = projectSettings.find(
        (s) => s.code === 'structure_orientation'
      );
      return orientationSetting?.value as ExposureDirectionType | undefined;
    }, [projectSettings]);

    const activeSpace = useMemo(() => {
      if (!activeItem?.uniqueId) return null;
      const item = findStructureItemById(structure, activeItem.uniqueId);
      if (!isSpaceType(item)) return null;
      return item;
    }, [structure, activeItem?.uniqueId]);

    const activeSpaceLevelFootprints = useMemo(() => {
      if (!activeItem?.uniqueId) return [];
      const level = findLevelBySpaceId(structure, activeItem.uniqueId);
      return getEffectiveFootprintPolygons(level);
    }, [structure, activeItem?.uniqueId]);

    useEffect(() => {
      setAttributesToApplyToLevel([]);
    }, [activeItem?.uniqueId]);

    useEffect(() => {
      setItemNameBuffer(itemName);
    }, [itemName]);

    useEffect(() => {
      const item = items.find((i) => i.id === activeItem?.itemId);

      if (item && (item.smallSize || item.mediumSize || item.largeSize)) {
        const newDefaultSizes: DefaultSizes = {};

        if (item.smallSize) {
          newDefaultSizes.Small = item.smallSize;
        }

        if (item.mediumSize) {
          newDefaultSizes.Medium = item.mediumSize;
        }

        if (item.largeSize) {
          newDefaultSizes.Large = item.largeSize;
        }

        setDefaultSizes(newDefaultSizes);
      } else {
        setDefaultSizes({});
      }
    }, [items, activeItem]);

    const determineNextAttributeIdToFocus = (
      attributeArray: AttributeType[]
    ): number | null => {
      const attributesToCheck = [
        attributeArray.find((a) => a.code === 'square_footage'),
        ...sortAttributes(
          filterAttributes(attributeArray, 'normal', 'Ceiling')
        ),
        attributeArray.find((a) => a.code === 'fenestration'),
        ...sortAttributes(filterAttributes(attributeArray, 'normal', false)),
        ...sortAttributes(filterAttributes(attributeArray, 'advanced', false))
      ].filter((a) => a) as AttributeType[];
      let newFocusedAttributeId: number | null = null;

      attributesToCheck
        .filter((a) => a.required)
        .forEach((attribute) => {
          if (newFocusedAttributeId === null && !attribute.value) {
            newFocusedAttributeId = attribute.id;
          }
        });

      return newFocusedAttributeId;
    };

    useEffect(() => {
      setIsFirstRender(true);

      if (!activeItem || !activeItem.attributes) {
        return;
      }

      setAttributesBuffer(activeItem?.attributes || []);
      setSizingAttributesBuffer(
        activeItem.sizingAttributes || defaultSizingAttributes
      );
      setFenestrationBuffer(activeItem.fenestration || []);
      setWidth(activeItem.width ?? undefined);
      setLength(activeItem.length ?? undefined);
      setSqFt(
        (activeItem.attributes.find((a) => a.code === 'square_footage')
          ?.value as number) || undefined
      );
      setSizingAttributesBuffer(
        activeItem.sizingAttributes || defaultSizingAttributes
      );
      setFenestrationBuffer(activeItem.fenestration || []);
      setItemNameBuffer(activeItem.label);

      const structureItem = findStructureItemById(
        structure,
        activeItem.uniqueId
      );
      if (isLevelType(structureItem) || isSpaceType(structureItem)) {
        setConditionedBuffer(structureItem.conditioned);
      }

      const spaceAdditionalBTUs = isSpaceType(structureItem)
        ? structureItem.additionalBTUs
        : undefined;
      setAdditionalBTUsBuffer({
        cooling: spaceAdditionalBTUs?.cooling ?? 0,
        heating: spaceAdditionalBTUs?.heating ?? 0
      });

      setFocusedAttributeId(
        determineNextAttributeIdToFocus(activeItem.attributes || [])
      );
    }, [activeItem]);

    const handleSave = () => {
      if (activeItem?.uniqueId) {
        const structureItem = findStructureItemById(
          structure,
          activeItem.uniqueId
        );

        if (structureItem) {
          const currentLevel = findLevelBySpaceId(
            structure,
            activeItem.uniqueId
          );
          const isSpace = isSpaceType(structureItem);
          const currentAdditionalBTUs = isSpace
            ? structureItem.additionalBTUs
            : undefined;
          const additionalBTUsChanged =
            isSpace &&
            ((currentAdditionalBTUs?.cooling ?? 0) !==
              additionalBTUsBuffer.cooling ||
              (currentAdditionalBTUs?.heating ?? 0) !==
                additionalBTUsBuffer.heating);

          const newStructureItem: LevelType | SectionType | SpaceType = {
            ...structureItem,
            attributes: attributesBuffer,
            sizingAttributes: sizingAttributesBuffer,
            modified: true,
            length,
            width,
            conditioned: conditionedBuffer,
            ...(additionalBTUsChanged
              ? { additionalBTUs: additionalBTUsBuffer }
              : {})
          };

          if (itemNameBuffer) {
            newStructureItem.label = itemNameBuffer;
          }

          dispatch(updateStructureItem(newStructureItem));

          if (attributesToApplyToLevel.length > 0 && currentLevel) {
            dispatch(
              applyAttributesToEntireLevel({
                levelId: currentLevel.uniqueId,
                attributes: attributesBuffer.filter((a) =>
                  attributesToApplyToLevel.includes(a.code)
                )
              })
            );
          }
        } else {
          console.error('Structure item not found');
          errorModal('Structure item not found');
        }
      } else {
        console.error('No item id provided');
        errorModal('No item id provided');
      }
    };

    const handleApplyToLevel = (
      checked: boolean,
      codes: AttributeCodeType[]
    ) => {
      if (checked) {
        setAttributesToApplyToLevel((old) => [...old, ...codes]);
      } else {
        setAttributesToApplyToLevel((old) =>
          old.filter((code) => !codes.includes(code))
        );
      }
    };

    const handleChangeAttribute = (attributeId: number, value: any) => {
      const newAttributes = produce(attributesBuffer, (draft) => {
        const updatedAttributes = draft;
        const index = updatedAttributes.findIndex((a) => a.id === attributeId);

        updatedAttributes[index].value = value;
        updatedAttributes[index].modified = true;

        return updatedAttributes;
      });

      setAttributesBuffer(newAttributes);
    };

    useEffect(() => {
      let timeout: NodeJS.Timeout | null = null;

      if (autosave && !isFirstRender) {
        timeout = setTimeout(() => {
          handleSave();
        }, 500);
      }

      setIsFirstRender(false);

      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }, [
      JSON.stringify(attributesBuffer),
      JSON.stringify(sizingAttributesBuffer),
      JSON.stringify(fenestrationBuffer),
      itemNameBuffer,
      conditionedBuffer,
      JSON.stringify(additionalBTUsBuffer)
    ]);

    useEffect(() => {
      if (length && width) {
        setSqFt(length * width);
      }
    }, [length, width]);

    useEffect(() => {
      const focusFenestrationId = activeItem?.focusFenestrationId;
      if (!focusFenestrationId) return undefined;

      const clearFenestrationFocusTip = () => {
        if (
          isFenestrationFocusTipSequence(
            store.getState().ui.activeTipSequence,
            focusFenestrationId
          )
        ) {
          dispatch(setActiveTipSequence(null));
        }
      };

      const showTimeout = setTimeout(() => {
        dispatch(
          setActiveTipSequence(
            buildFenestrationFocusTipSequence(focusFenestrationId)
          )
        );
      }, 200);
      const hideTimeout = setTimeout(clearFenestrationFocusTip, 2200);

      return () => {
        clearTimeout(showTimeout);
        clearTimeout(hideTimeout);
        clearFenestrationFocusTip();
      };
    }, [activeItem?.focusFenestrationId, dispatch, store]);

    const handleSqFtChange = (newSqFt: number) => {
      setSqFt(newSqFt);
      if (newSqFt && newSqFt !== (length ?? 0) * (width ?? 0)) {
        setLength(undefined);
        setWidth(undefined);
      }

      const sqFtAttribute = attributesBuffer.find(
        (a) => a.code === 'square_footage'
      );

      if (sqFtAttribute) {
        handleChangeAttribute(sqFtAttribute.id, newSqFt || undefined);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        const newFocusedAttributeId =
          determineNextAttributeIdToFocus(attributesBuffer);

        if (newFocusedAttributeId !== null) {
          setFocusedAttributeId(newFocusedAttributeId);
        } else if (!autosave) {
          handleSave();
        }
      }
    };

    const getUnsavedChanges = () => {
      const structureItem = activeItem?.uniqueId
        ? findStructureItemById(structure, activeItem.uniqueId)
        : null;
      const isSpace = isSpaceType(structureItem);

      return hasActiveItemUnsavedChanges({
        attributesBuffer,
        savedAttributes: activeItem?.attributes,
        sizingAttributesBuffer,
        savedSizingAttributes: activeItem?.sizingAttributes,
        itemNameBuffer,
        savedItemName: activeItem?.label,
        conditionedBuffer,
        savedConditioned:
          isLevelType(structureItem) || isSpace
            ? structureItem.conditioned
            : undefined,
        compareConditioned: isLevelType(structureItem) || isSpace,
        additionalBTUsBuffer,
        savedAdditionalBTUs: isSpace ? structureItem.additionalBTUs : undefined,
        compareAdditionalBTUs: isSpace
      });
    };

    useImperativeHandle(ref, () => ({
      handleSave,
      hasUnsavedChanges: getUnsavedChanges
    }));

    const showSizingOnly = activeItem?.showSizingOnly || false;

    const isCrawlspace = useMemo(() => {
      if (!activeItem?.uniqueId) return false;
      const structureItem = findStructureItemById(
        structure,
        activeItem.uniqueId
      );
      return isLevelType(structureItem) && structureItem.type === 'crawlspace';
    }, [activeItem?.uniqueId, structure]);

    const isSpaceInUnconditionedLevel = useMemo(() => {
      if (!activeItem?.uniqueId) return false;
      const parentLevel = findLevelBySpaceId(structure, activeItem.uniqueId);
      return parentLevel !== null && !isLevelConditioned(parentLevel);
    }, [activeItem?.uniqueId, structure]);

    const isLevel = useMemo(() => {
      if (!activeItem?.uniqueId) return false;
      const structureItem = findStructureItemById(
        structure,
        activeItem.uniqueId
      );
      return isLevelType(structureItem);
    }, [activeItem?.uniqueId, structure]);

    const isSpace = useMemo(() => {
      if (!activeItem?.uniqueId) return false;
      const structureItem = findStructureItemById(
        structure,
        activeItem.uniqueId
      );
      return isSpaceType(structureItem);
    }, [activeItem?.uniqueId, structure]);

    const spaceDetailsCategory =
      currentSectionId === 'space_details'
        ? getSpaceDetailsSubSteps(structure)[activeSubStepIndex]?.category
        : undefined;
    const sizingOrder = getSizingOrder(currentSectionId, spaceDetailsCategory);
    const showSizingFirst = sizingOrder !== 'default';

    const renderDimensionsSection = () => {
      if (
        showSizingOnly ||
        !attributesBuffer.some(
          (a) =>
            dimensionsAttributeCodes.includes(a.code) ||
            a.code === 'square_footage'
        )
      ) {
        return null;
      }

      return (
        <>
          <div className='flex items-center justify-between'>
            <div className='text-xl font-bold text-gray-900'>Dimensions</div>
            <label className='hidden cursor-pointer items-center gap-2'>
              <Checkbox
                onChange={(checked) =>
                  handleApplyToLevel(checked, dimensionsAttributeCodes)
                }
                value={attributesBuffer
                  .filter((a) => dimensionsAttributeCodes.includes(a.code))
                  .some((a) => attributesToApplyToLevel.includes(a.code))}
              />
              <span className='text-sm font-semibold'>Apply to level</span>
            </label>
          </div>
          <div className='flex flex-col gap-4 rounded-lg bg-blue-100 p-4'>
            <div className='flex items-end gap-4'>
              <div className='flex w-full flex-col gap-2'>
                <div className='flex items-center gap-2'>
                  <ProgressIndicator
                    required={
                      attributesBuffer.find(
                        (att) => att.code === 'square_footage'
                      )?.required
                    }
                    checked={!!(sqFt || (length && width))}
                  />
                  <div className='font-semibold text-gray-800'>
                    Square Footage
                  </div>
                </div>
                <div className='inline-flex gap-3'>
                  {Object.keys(defaultSizes).length > 0 && (
                    <Select
                      options={[
                        { label: 'Custom', value: '' },
                        ...Object.entries(defaultSizes).map(([key, value]) => ({
                          label: key,
                          value: value.toString()
                        }))
                      ]}
                      onChange={(value) => handleSqFtChange(Number(value))}
                      value={sqFt?.toString() || undefined}
                      disabled
                    />
                  )}
                  <NumberInput
                    placeholder='0 sqft'
                    onChange={(value) => handleSqFtChange(value ?? 0)}
                    value={sqFt || undefined}
                    min={0}
                    autoFocus={
                      focusedAttributeId ===
                      attributesBuffer.find((a) => a.code === 'square_footage')
                        ?.id
                    }
                    suffix=' sqft'
                    disabled
                  />
                </div>
              </div>
              <span className='py-2 font-semibold'>OR</span>
              <div className='w-full'>
                <div className='flex items-center gap-2'>
                  <NumberInput
                    placeholder='0 ft'
                    onChange={(value) => setWidth(value)}
                    value={width || undefined}
                    min={0}
                    suffix=' ft'
                    disabled
                  />
                  <div className='h-6 w-6'>
                    <XMarkIcon className='h-6 w-6' />
                  </div>
                  <NumberInput
                    placeholder='0 ft'
                    onChange={(value) => setLength(value)}
                    value={length || undefined}
                    min={0}
                    suffix=' ft'
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className='flex flex-wrap gap-4'>
              {sortAttributes(
                filterAttributes(
                  attributesBuffer.filter((a) =>
                    dimensionsAttributeCodes.includes(
                      a.code as AttributeCodeType
                    )
                  )
                )
              ).map((attribute) => (
                <div
                  key={attribute.id}
                  className='flex max-w-[calc(50%-.5rem)] flex-shrink grow basis-1/3 flex-col gap-2'
                >
                  <AttributeComponent
                    onChange={(value) =>
                      handleChangeAttribute(attribute.id, value)
                    }
                    completed={!!attribute.value}
                    autoFocus={attribute.id === focusedAttributeId}
                    {...{ ...attribute }}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      );
    };

    const renderAttributesSection = () => {
      const hasNormalAttributes =
        !showSizingOnly &&
        filterAttributes(
          attributesBuffer.filter((a) => a.group !== 'Sizing'),
          'normal'
        ).length > 0;

      return (
        <>
          <div className='text-xl font-bold text-gray-900'>Attributes</div>
          {hasNormalAttributes && (
            <div className='flex flex-wrap gap-4'>
              {sortAttributes(
                filterAttributes(
                  attributesBuffer.filter((a) => a.group !== 'Sizing'),
                  'normal'
                )
              ).map((attribute) => (
                <div
                  key={attribute.id}
                  className='flex max-w-[calc(50%-.5rem)] flex-shrink grow basis-1/3 flex-col gap-2'
                >
                  <AttributeComponent
                    onChange={(value) =>
                      handleChangeAttribute(attribute.id, value)
                    }
                    completed={isNotEmpty(attribute.value)}
                    autoFocus={attribute.id === focusedAttributeId}
                    {...{ ...attribute }}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      );
    };

    const renderConditionedSection = () => (
      <div className='relative w-fit'>
        <label className='flex cursor-pointer items-center gap-2'>
          <Checkbox
            onChange={(checked) => setConditionedBuffer(checked)}
            value={conditionedBuffer ?? false}
            disabled={isCrawlspace || isSpaceInUnconditionedLevel}
          />
          <span className='font-semibold text-gray-800'>Conditioned</span>
        </label>
        {isCrawlspace && (
          <Tooltip text='Cannot set crawlspace level as conditioned' />
        )}
        {isSpaceInUnconditionedLevel && !isCrawlspace && (
          <Tooltip text='Cannot condition space in an unconditioned level' />
        )}
      </div>
    );

    const renderAdditionalBTUsSection = () => {
      if (!isSpace || showSizingOnly) return null;

      return (
        <>
          <div className='text-xl font-bold text-gray-900'>Additional BTUs</div>
          <div className='flex flex-col gap-4 rounded-lg bg-blue-100 p-4'>
            <div className='flex flex-wrap gap-4'>
              <div className='flex max-w-[calc(50%-.5rem)] flex-shrink grow basis-1/3 flex-col gap-2'>
                <div className='font-semibold text-gray-800'>
                  Cooling BTU Increase
                </div>
                <NumberInput
                  placeholder='0 BTU/h'
                  onChange={(value) =>
                    setAdditionalBTUsBuffer((prev) => ({
                      ...prev,
                      cooling: value ?? 0
                    }))
                  }
                  value={additionalBTUsBuffer.cooling || undefined}
                  suffix=' BTU/h'
                />
              </div>
              <div className='flex max-w-[calc(50%-.5rem)] flex-shrink grow basis-1/3 flex-col gap-2'>
                <div className='font-semibold text-gray-800'>
                  Heating BTU Increase
                </div>
                <NumberInput
                  placeholder='0 BTU/h'
                  onChange={(value) =>
                    setAdditionalBTUsBuffer((prev) => ({
                      ...prev,
                      heating: value ?? 0
                    }))
                  }
                  value={additionalBTUsBuffer.heating || undefined}
                  suffix=' BTU/h'
                />
              </div>
            </div>
          </div>
        </>
      );
    };

    const renderSizingSection = () => {
      const shouldShowSizing =
        showSizingOnly ||
        filterAttributes(
          attributesBuffer.filter((a) => a.group !== 'Sizing'),
          'advanced'
        ).length > 0;

      if (!shouldShowSizing) return null;

      const defaultArea = Math.round(
        getAttributeValue(attributesBuffer, 'square_footage') ?? 0
      );
      const defaultWallHeight =
        getAttributeValue(attributesBuffer, 'ceiling_height') ?? 0;
      const defaultWallWidth = Math.ceil(Math.sqrt(defaultArea));

      const currentSizingAttrs =
        JSON.stringify(sizingAttributesBuffer) !==
        JSON.stringify(activeItem?.sizingAttributes)
          ? sizingAttributesBuffer
          : activeItem?.sizingAttributes;

      return (
        <div className='flex flex-wrap gap-4'>
          <SizingItems
            sizingAttributes={currentSizingAttrs}
            updateSizingAttributes={updateSizingAttributes}
            defaultArea={defaultArea}
            defaultWallHeight={defaultWallHeight}
            defaultWallWidth={defaultWallWidth}
            orderType={sizingOrder}
            excludeWalls
            excludeCeilings
            excludeFloors
          />
          <WallsAroundSpace
            space={activeSpace}
            footprints={activeSpaceLevelFootprints}
            sizingAttributes={currentSizingAttrs}
            updateSizingAttributes={updateSizingAttributes}
            homeOrientation={homeOrientation}
            defaultArea={defaultArea}
            defaultWallWidth={defaultWallWidth}
            defaultWallHeight={defaultWallHeight}
            focusFenestrationId={activeItem?.focusFenestrationId}
          />
        </div>
      );
    };

    const renderAdvancedSection = () => {
      const hasAdvancedAttributes =
        !showSizingOnly &&
        filterAttributes(
          attributesBuffer.filter((a) => a.group !== 'Sizing'),
          'advanced'
        ).length > 0;

      if (!hasAdvancedAttributes) return null;

      return (
        <>
          <div className='text-xl font-bold text-gray-900'>
            Advanced Settings
          </div>
          <div className='flex w-full flex-wrap gap-4'>
            {sortAttributes(
              filterAttributes(
                attributesBuffer.filter((a) => a.group !== 'Sizing'),
                'advanced'
              )
            ).map((attribute) => (
              <div
                key={attribute.id}
                className='flex max-w-[calc(50%-.5rem)] flex-shrink grow basis-1/3 flex-col gap-2'
              >
                <AttributeComponent
                  onChange={(value) =>
                    handleChangeAttribute(attribute.id, value)
                  }
                  completed={isNotEmpty(attribute.value)}
                  autoFocus={attribute.id === focusedAttributeId}
                  {...{ ...attribute }}
                />
              </div>
            ))}
          </div>
        </>
      );
    };

    return (
      <div
        ref={animate}
        className='flex w-full flex-col gap-3'
        onKeyDown={handleKeyDown}
      >
        {allowRename && (
          <div>
            <Text
              onInput={(value) => setItemNameBuffer(value)}
              value={itemNameBuffer}
              placeholder='Name'
            />
          </div>
        )}
        {showSizingFirst ? (
          <>
            {!isLevel && renderSizingSection()}
            {renderDimensionsSection()}
            {renderAttributesSection()}
            {renderAdditionalBTUsSection()}
            {renderConditionedSection()}
            {renderAdvancedSection()}
          </>
        ) : (
          <>
            {renderDimensionsSection()}
            {renderAttributesSection()}
            {renderAdditionalBTUsSection()}
            {renderConditionedSection()}
            {!isLevel && renderSizingSection()}
            {renderAdvancedSection()}
          </>
        )}
      </div>
    );
  }
);

ActiveItemAttributes.displayName = 'ActiveItemAttributes';

export default ActiveItemAttributes;
